import { useState, useEffect } from 'react';
import firebase from 'firebase/compat/app';
import './styles.css';

const fmtForStore = (dateStr) => dateStr.replace(/-/g, ''); // "2025-01-01" → "20250101"

const fmtDisplay = (s) =>
  `${s.slice(0, 4)}年${s.slice(4, 6)}月${s.slice(6, 8)}日`;

const Admin = () => {
  // ニュース
  const [news, setNews] = useState([
    { date: '', text: '' },
    { date: '', text: '' },
    { date: '', text: '' },
  ]);
  const [newsSaving, setNewsSaving] = useState(false);
  const [newsSaved, setNewsSaved] = useState(false);

  // 定休日
  const [closedDays, setClosedDays] = useState([]);
  const [newDate, setNewDate] = useState('');
  const [closedSaving, setClosedSaving] = useState(false);
  const [closedSaved, setClosedSaved] = useState(false);

  const [loadError, setLoadError] = useState('');

  // noindex でクローラーをブロック
  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    return () => document.head.removeChild(meta);
  }, []);

  // ニュース読み込み
  useEffect(() => {
    const toDateStr = (ts) => {
      if (!ts || !ts.seconds) return '';
      return new Date(ts.seconds * 1000).toISOString().split('T')[0];
    };
    firebase.firestore().collection('news').doc('items').get()
      .then((doc) => {
        if (!doc.exists) return;
        const d = doc.data();
        setNews([
          { date: toDateStr(d['news①']?.[0]), text: d['news①']?.[1] || '' },
          { date: toDateStr(d['news②']?.[0]), text: d['news②']?.[1] || '' },
          { date: toDateStr(d['news③']?.[0]), text: d['news③']?.[1] || '' },
        ]);
      })
      .catch(() => setLoadError('ニュースの読み込みに失敗しました'));
  }, []);

  // 定休日読み込み
  useEffect(() => {
    firebase.firestore().collection('calendar').doc('items').get()
      .then((doc) => {
        if (doc.exists && Array.isArray(doc.data().closedDays)) {
          const sorted = [...doc.data().closedDays].sort();
          setClosedDays(sorted);
        }
      })
      .catch(() => setLoadError('定休日の読み込みに失敗しました'));
  }, []);

  const saveNews = async () => {
    setNewsSaving(true);
    setNewsSaved(false);
    try {
      const toTs = (s) => firebase.firestore.Timestamp.fromDate(new Date(s));
      await firebase.firestore().collection('news').doc('items').update({
        'news①': [toTs(news[0].date), news[0].text],
        'news②': [toTs(news[1].date), news[1].text],
        'news③': [toTs(news[2].date), news[2].text],
      });
      setNewsSaved(true);
      setTimeout(() => setNewsSaved(false), 3000);
    } catch (e) {
      alert(`保存に失敗しました\n\n${e.message || e}`);
    } finally {
      setNewsSaving(false);
    }
  };

  const addDate = () => {
    if (!newDate) return;
    const s = fmtForStore(newDate);
    if (closedDays.includes(s)) return;
    setClosedDays((prev) => [...prev, s].sort());
    setNewDate('');
  };

  const removeDate = (s) => {
    setClosedDays((prev) => prev.filter((d) => d !== s));
  };

  const saveClosed = async () => {
    setClosedSaving(true);
    setClosedSaved(false);
    try {
      await firebase.firestore().collection('calendar').doc('items').set({ closedDays });
      setClosedSaved(true);
      setTimeout(() => setClosedSaved(false), 3000);
    } catch (e) {
      alert(`保存に失敗しました\n\n${e.message || e}`);
    } finally {
      setClosedSaving(false);
    }
  };

  const updateNews = (i, field, val) => {
    const next = [...news];
    next[i] = { ...next[i], [field]: val };
    setNews(next);
  };

  return (
    <div className="admin-wrap">
      <div className="admin-container">
        <div className="admin-header">
          <h1>お知らせ管理</h1>
        </div>

        {loadError && <p className="admin-error">{loadError}</p>}

        {/* ニュース */}
        <section className="admin-section">
          <h2>ニュース</h2>
          <p className="admin-hint">日付とニュース本文を入力してください</p>
          {news.map((n, i) => (
            <div className="admin-news-row" key={i}>
              <span className="admin-news-label">ニュース {i + 1}</span>
              <div className="admin-news-fields">
                <div className="admin-field">
                  <label>日付</label>
                  <input
                    type="date"
                    value={n.date}
                    onChange={(e) => updateNews(i, 'date', e.target.value)}
                  />
                </div>
                <div className="admin-field admin-field-grow">
                  <label>内容</label>
                  <input
                    type="text"
                    value={n.text}
                    onChange={(e) => updateNews(i, 'text', e.target.value)}
                    placeholder="ニュース内容を入力"
                  />
                </div>
              </div>
            </div>
          ))}
          <div className="admin-section-footer">
            <button
              className={`admin-btn-primary ${newsSaved ? 'admin-btn-saved' : ''}`}
              onClick={saveNews}
              disabled={newsSaving}
            >
              {newsSaving ? '保存中...' : newsSaved ? '✓ 保存しました' : '保存する'}
            </button>
          </div>
        </section>

        {/* 定休日 */}
        <section className="admin-section">
          <h2>定休日</h2>
          <p className="admin-hint">カレンダーに「定休日」と表示される日付を管理します</p>

          <div className="admin-date-add">
            <input
              type="date"
              value={newDate}
              onChange={(e) => setNewDate(e.target.value)}
            />
            <button className="admin-btn-add" onClick={addDate}>追加</button>
          </div>

          <div className="admin-closed-list">
            {closedDays.length === 0 && (
              <p className="admin-empty">定休日が登録されていません</p>
            )}
            {closedDays.map((s) => (
              <div className="admin-closed-item" key={s}>
                <span>{fmtDisplay(s)}</span>
                <button className="admin-btn-remove" onClick={() => removeDate(s)}>×</button>
              </div>
            ))}
          </div>

          <div className="admin-section-footer">
            <button
              className={`admin-btn-primary ${closedSaved ? 'admin-btn-saved' : ''}`}
              onClick={saveClosed}
              disabled={closedSaving}
            >
              {closedSaving ? '保存中...' : closedSaved ? '✓ 保存しました' : '保存する'}
            </button>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Admin;
